import Intercom from "@intercom/messenger-js-sdk";
import { useEffect, useRef } from "react";
import { getIntercomUserHash } from "../api/services/intercomService";
import type { IntercomUser } from "../types/intercom";

const DEFAULT_INTERCOM_APP_ID = "k5nnj6yq";
const INTERCOM_STYLE_ID = "2721503";

interface UseIntercomOptions {
	user: IntercomUser | null;
	enabled?: boolean;
	appId?: string;
}

export function useIntercom(options: UseIntercomOptions): void {
	const { user, enabled = true, appId } = options;
	const initRef = useRef(false);
	const userNameRef = useRef<string | undefined>(undefined);

	useEffect(() => {
		if (!enabled || !user?.email || !user?.instanceId || initRef.current) {
			return;
		}

		const resolvedAppId = appId ?? DEFAULT_INTERCOM_APP_ID;
		const userId = `${user.instanceId}-${user.email}`.toLowerCase();

		const initIntercom = async () => {
			let userHash: string | undefined;
			try {
				const response = await getIntercomUserHash();
				userHash = response.userHash;
			} catch {
				// Proceed without hash (best-effort identity verification)
			}

			Intercom({
				app_id: resolvedAppId,
				user_id: userId,
				email: user.email,
				name: user.name,
				user_hash: userHash,
				messenger_style_id: INTERCOM_STYLE_ID,
			});

			initRef.current = true;
			userNameRef.current = user.name;
		};

		initIntercom();

		const handleBeforeUnload = () => {
			if (initRef.current && window.Intercom) {
				window.Intercom("shutdown");
			}
		};

		window.addEventListener("beforeunload", handleBeforeUnload);

		return () => {
			window.removeEventListener("beforeunload", handleBeforeUnload);
			if (initRef.current && window.Intercom) {
				window.Intercom("shutdown");
				initRef.current = false;
			}
		};
	}, [user, enabled, appId]);

	// Push name updates after init
	useEffect(() => {
		if (
			!enabled ||
			!initRef.current ||
			!user?.name ||
			user.name === userNameRef.current
		) {
			return;
		}
		if (window.Intercom) {
			window.Intercom("update", { name: user.name });
			userNameRef.current = user.name;
		}
	}, [user?.name, enabled]);
}

declare global {
	interface Window {
		Intercom?: (command: string, ...args: unknown[]) => void;
	}
}
